import { useRef, useState, useEffect } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import './GraphView.css';

const GraphView = ({ markdownFiles, onNodeClick }) => {
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [hoveredNode, setHoveredNode] = useState(null);
  const [showLegend, setShowLegend] = useState(true);
  const fgRef = useRef();

  useEffect(() => {
    const processGraphData = () => {
      const nodes = [
        {
          id: 'saboten',
          name: 'Saboten',
          val: 12,
          color: '#ff6b6b',
          group: 'central'
        }
      ];

      markdownFiles.forEach(file => {
        nodes.push({
          id: file.title,
          name: file.title,
          val: 6,
          color: '#8884d8',
          group: 'document',
          data: file
        });
      });

      // Create links
      const links = [];
      
      // Connect all files to Saboten
      markdownFiles.forEach(file => {
        links.push({
          source: 'saboten',
          target: file.title,
          value: 1
        });
      });

      // Add links between files based on [[wiki-links]]
      markdownFiles.forEach(file => {
        const wikiLinks = (file.content.match(/\[\[(.*?)\]\]/g) || [])
          .map(link => link.replace(/\[\[|\]\]/g, ''));
        
        wikiLinks.forEach(linkedTitle => {
          if (markdownFiles.some(f => f.title === linkedTitle)) {
            links.push({
              source: file.title,
              target: linkedTitle,
              value: 1
            });
          }
        });
      });

      setGraphData({ nodes, links });
    };

    processGraphData();
  }, [markdownFiles]);

  useEffect(() => {
    // Zoom to fit when data changes
    if (fgRef.current) {
      fgRef.current.zoomToFit(400, 50);
    }
  }, [graphData]);

  const handleNodeClick = node => {
    if (node.id !== 'saboten' && onNodeClick) {
      onNodeClick(node.data);
    }
  };

  const handleNodeHover = node => {
    setHoveredNode(node);
    if (fgRef.current) {
      fgRef.current.d3ReheatSimulation();
    }
  };

  const handleZoomIn = () => {
    if (fgRef.current) {
      fgRef.current.zoom(fgRef.current.zoom() * 1.5);
    }
  };

  const handleZoomOut = () => {
    if (fgRef.current) {
      fgRef.current.zoom(fgRef.current.zoom() / 1.5);
    }
  };

  const handleCenterView = () => {
    if (fgRef.current) {
      fgRef.current.zoomToFit(400);
    }
  };

  return (
    <div className="graph-view">
      <div className="graph-header">
        <h2 className="graph-title">All the Markdown Notes</h2>
        <div className="graph-controls">
          <button 
            className="graph-button"
            onClick={() => setShowLegend(!showLegend)}
          >
            {showLegend ? 'Hide Legend' : 'Show Legend'}
          </button>
          <button 
            className="graph-button"
            onClick={handleCenterView}
          >
            Center View
          </button>
        </div>
      </div>
      
      <div className="graph-container">
        {showLegend && (
          <div className="graph-legend">
            <div className="legend-item">
              <div className="legend-color" style={{ background: '#ff6b6b' }} />
              <span>Central Node</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ background: '#8884d8' }} />
              <span>Document Node</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ background: '#d1d8e0' }} />
              <span>Connection</span>
            </div>
          </div>
        )}
        
        <div className="zoom-controls">
          <button className="zoom-button" onClick={handleZoomIn}>+</button>
          <button className="zoom-button" onClick={handleZoomOut}>-</button>
        </div>

        <ForceGraph2D
          ref={fgRef}
          graphData={graphData}
          nodeLabel="name"
          nodeRelSize={6}
          linkDirectionalParticles={2}
          linkDirectionalParticleSpeed={d => d.value * 0.01}
          d3VelocityDecay={0.4}
          cooldownTicks={50}
          onEngineStop={() => fgRef.current.zoomToFit(400)}
          onNodeClick={handleNodeClick}
          onNodeHover={handleNodeHover}
          nodeCanvasObject={(node, ctx, globalScale) => {
            const label = node.name;
            const fontSize = node.val * (node === hoveredNode ? 1.2 : 0.8);
            const nodeSize = node.val * (node === hoveredNode ? 1.2 : 1);
            
            // Draw node shadow
            ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
            ctx.shadowBlur = 5;
            ctx.shadowOffsetX = 2;
            ctx.shadowOffsetY = 2;

            // Draw node
            ctx.beginPath();
            ctx.arc(node.x, node.y, nodeSize, 0, 2 * Math.PI, false);
            ctx.fillStyle = node === hoveredNode 
              ? `${node.color}ee`
              : `${node.color}cc`;
            ctx.fill();

            // Reset shadow
            ctx.shadowColor = 'transparent';

            // Draw label
            if (globalScale >= 0.8 || node === hoveredNode) {
              ctx.font = `${fontSize}px Inter, system-ui, -apple-system, sans-serif`;
              ctx.fillStyle = 'white';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(label, node.x, node.y);
            }

            // Add glow effect on hover
            if (node === hoveredNode) {
              ctx.beginPath();
              ctx.arc(node.x, node.y, nodeSize + 3, 0, 2 * Math.PI, false);
              ctx.strokeStyle = node.color;
              ctx.lineWidth = 2;
              ctx.stroke();
            }
          }}
          linkColor={() => '#d1d8e0'}
          linkWidth={link => link.value * 0.5}
          linkDirectionalParticleWidth={2}
          d3AlphaDecay={0.02}
          d3AlphaMin={0.001}
          warmupTicks={100}
          linkDirectionalParticleColor={() => '#8884d8'}
        />
      </div>
    </div>
  );
};

export default GraphView;